// Centralized PostHog event helpers for the Projection Lab + top-level feature
// navigation and the Tier Builder.
//
// Mirrors src/utils/mockAnalytics.js: event names and property shapes live in
// one place, posthog-js is imported once, and no PII (no league names, no
// emails, no user-entered notes) ever lands in properties. Only team abbrevs,
// counts, ranks, and totals are captured.
//
// PostHog itself is initialized in src/App.js with the project key; safeCapture
// inherits the App-level analytics gate and never throws.

import posthog from "posthog-js";

// Analytics must never break a user flow — swallow capture failures.
function safeCapture(event, properties) {
  try {
    posthog.capture(event, properties);
  } catch {
    /* ignore */
  }
}

// ----------------------------------------------------------------------------
// TOP-LEVEL FEATURE NAVIGATION
// ----------------------------------------------------------------------------

// Fired when a user opens one of the top-level features from the landing page.
// feature ∈ team_builder | tiers | mock_draft | almanac | projection_lab
export function captureFeatureView({ feature }) {
  safeCapture("feature_view", {
    feature: feature ?? null,
  });
}

// ----------------------------------------------------------------------------
// PROJECTION LAB FUNNEL
// ----------------------------------------------------------------------------

// Fired when the Projection Lab is opened (the team grid renders).
export function captureProjlabOpened() {
  safeCapture("projlab_opened", {});
}

// Fired when a user starts projecting a team (enters the editor for it).
export function captureProjlabTeamStarted({ team }) {
  safeCapture("projlab_team_started", {
    team: team ?? null,
  });
}

// Fired when a team's projection reaches the results page (all four steps done).
export function captureProjlabTeamCompleted({ team, teamTotal, projRank }) {
  safeCapture("projlab_team_completed", {
    team: team ?? null,
    team_total: teamTotal ?? null,
    proj_rank: projRank ?? null,
  });
}

// Fired when the projected Rankings view is opened.
export function captureProjlabRankingsViewed() {
  safeCapture("projlab_rankings_viewed", {});
}

// Fired when the user clicks "Build Draft Values" from the Rankings view.
export function captureProjlabBuildDraftValues({ teamsProjected }) {
  safeCapture("projlab_build_draft_values", {
    teams_projected: teamsProjected ?? null,
  });
}

// ----------------------------------------------------------------------------
// TIER BUILDER
// ----------------------------------------------------------------------------

// Fired when a player is moved between tiers, or reordered within one.
// method ∈ drag | tap (desktop drag-and-drop vs. the mobile tap-to-move path).
// Event name is unchanged from the original tier-analytics ship so saved
// insights keep resolving; method is additive.
export function captureTierPlayerMoved({ method } = {}) {
  safeCapture("tier_player_moved", {
    method: method ?? null,
  });
}

// Fired when the tier cheat sheet is opened.
export function captureTierCheatSheet() {
  safeCapture("tier_cheat_sheet_opened", {});
}
